import React from 'react';

import { Badge } from '../Badge';
import { cn } from '../cn';
import { ContentSection } from '../ContentSection';
import { InteractionStep, type InteractionAction } from '../HistoryStep';
import { Button } from '../Input2';
import { SmallPadded, SmallPaddedScrollable } from '../Scrollable';
import { Alerts, Arrows, General, Time } from '../StrokeIcons';
import { WaveSpinner } from '../WaveSpinner';
import { weekDelayLabel } from '../CampaignDayCard/weekDelayLabel';

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// ============================================================================
// Local flat enums (mirror the pg-gen unions; UI components must not import
// server/DB types — the container's tRPC shapes assign structurally)
// ============================================================================

type CadenceState =
  | 'not-started'
  | 'active'
  | 'paused'
  | 'finished'
  | 'interrupted'
  | 'action-required'
  | 'responded'
  | 'deleted'
  | 'researching'
  | 'pending-review'
  | 'rejected'
  | 'contact_unavailable';

type CadenceStepState =
  | 'not-started'
  | 'interrupted'
  | 'skipped'
  | 'finished'
  | 'canceled'
  | 'action-required'
  | 'responded'
  | 'contact_unavailable';

type CadenceStepType =
  | 'message'
  | 'connect'
  | 'inmail'
  | 'visit'
  | 'follow'
  | 'email'
  | 'reminder'
  | 'call'
  | 'delay'
  | 'likeRecentPosts'
  | 'wait_for_connect'
  | 'voice_note'
  | 'sift';

type CadenceStepOption = 'one' | 'two' | 'three';

// ============================================================================
// Public prop types (mapped from router output by the container — kept local so
// the Pure stays decoupled from the API/pg-gen layer)
// ============================================================================

export type CampaignDetailHeader = {
  id: string;
  name: string | null;
  state: CadenceState;
  createdOn: Date | string | null;
  stepsRun: number | null;
  stepsTotalCount: number;
};

export type CampaignDetailStep = {
  id: string;
  parent_id: string | null;
  option: CadenceStepOption;
  type: CadenceStepType;
  state: CadenceStepState;
  delay: number;
  /** When true (delay nodes), the delay is calendar-week aligned. */
  delayAlignWeek?: boolean;
  completedOn: Date | null;
  errorMessage: string | null;
  skipReason: string | null;
  internalErrorMessage: string | null;
};

export type CampaignDetailHistoryItem = {
  interaction: {
    id: string | number;
    userId: string;
    interactionAction: string;
    date: Date;
    updatedOn: Date;
    subject?: string | null;
    body?: string | null;
    from?: string | null;
    to?: string | null;
    data_url?: string | null;
    data_url_type?: 'audio' | 'video' | 'img' | 'file' | null;
  };
  interactionUser?: { name?: string | null; email?: string | null } | null;
  documentView?: {
    doc_id?: string;
    title?: string | null;
    description?: string | null;
    og_image_url?: string | null;
    fp_ip_loc_city_name?: string | null;
    fp_ip_loc_country_name?: string | null;
    fp_browser_name?: string | null;
    fp_os?: string | null;
  } | null;
  cadence?: { name?: string | null } | null;
  contact?: { first_name?: string | null; last_name?: string | null } | null;
};

export type CampaignDetailPagePureProps = {
  currentUserId: string;
  /** undefined → "Campaign not found" state. */
  campaignHeader?: CampaignDetailHeader;
  steps: CampaignDetailStep[];
  interruptMessage: string | null;
  interruptReason: string | null;
  history: CampaignDetailHistoryItem[];
  /** Header/steps still loading. */
  isLoading: boolean;
  /** History list still loading. */
  isHistoryLoading: boolean;
  onBack: () => void;
};

// ============================================================================
// Graph types + helpers (read-only display of steps, incl. archived)
// ============================================================================

type CadenceGraphNode<T> = T & {
  next: Partial<Record<CadenceStepOption, string>>;
  group: string[];
};
type CadenceGraph<S> = {
  nodes: Record<string, CadenceGraphNode<S>>;
  startId: string;
};

function getStateLabel(state: CadenceState): string {
  const labels: Record<CadenceState, string> = {
    'active': 'Active',
    'paused': 'Paused',
    'finished': 'Finished',
    'interrupted': 'Stopped',
    'responded': 'Replied',
    'deleted': 'Deleted',
    'not-started': 'Not Started',
    'pending-review': 'Pending Review',
    'rejected': 'Rejected',
    'contact_unavailable': 'Unavailable',
    'action-required': 'Action Required',
    'researching': 'Researching',
  };
  return labels[state] ?? state;
}

function getStateBadgeType(state: CadenceState): 'success' | 'warning' | 'dangerous' | 'standard' {
  const types: Record<CadenceState, 'success' | 'warning' | 'dangerous' | 'standard'> = {
    'active': 'success',
    'paused': 'warning',
    'finished': 'standard',
    'interrupted': 'dangerous',
    'responded': 'success',
    'deleted': 'dangerous',
    'not-started': 'standard',
    'pending-review': 'warning',
    'rejected': 'dangerous',
    'contact_unavailable': 'dangerous',
    'action-required': 'warning',
    'researching': 'warning',
  };
  return types[state] ?? 'standard';
}

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Build step graph INCLUDING archived steps. Unlike cadenceStepBuildGraph this
 * keeps archived steps so historical campaigns show every step.
 */
function buildStepGraphIncludingArchived<
  S extends {
    id: string;
    parent_id: string | null;
    option: CadenceStepOption;
    type: CadenceStepType;
  },
>(steps: S[]): CadenceGraph<S> | null {
  let startId: string | undefined;

  const nodes: Record<string, CadenceGraphNode<S>> = {};
  steps
    .map((s) => Object.assign({}, s, { next: {}, group: [] }))
    .forEach((s) => {
      nodes[s.id] = s;
    });

  steps.forEach((step) => {
    if (step.parent_id == null) {
      startId = step.id;
      return;
    }

    const parentNode = nodes[step.parent_id];
    if (parentNode?.type === 'delay') {
      if (step.type === 'delay' || step.type === 'wait_for_connect') {
        parentNode.next[step.option] = step.id;
      } else {
        parentNode.group.push(step.id);
      }
    }
    if (parentNode?.type === 'wait_for_connect') {
      parentNode.next[step.option] = step.id;
    }
  });

  if (startId == null) return null;

  return {
    nodes,
    startId,
  };
}

/**
 * Get IDs of all nodes reachable from the start node via the graph structure.
 * Used to find orphaned steps that are missing parent relationships.
 */
function getReachableNodeIds(graph: CadenceGraph<CampaignDetailStep>): Set<string> {
  const visited = new Set<string>();

  function visit(nodeId: string) {
    if (visited.has(nodeId)) return;
    const node = graph.nodes[nodeId];
    if (!node) return;

    visited.add(nodeId);
    node.group.forEach(visit);
    if (node.next.one) visit(node.next.one);
    if (node.next.two) visit(node.next.two);
  }

  if (graph.startId) {
    visit(graph.startId);
  }

  return visited;
}

const stepTypeLabels: Record<CadenceStepType, string> = {
  message: 'Message',
  connect: 'Connect',
  inmail: 'InMail',
  visit: 'Visit Profile',
  follow: 'Follow',
  email: 'Email',
  reminder: 'Reminder',
  call: 'Call',
  delay: 'Delay',
  likeRecentPosts: 'Like Posts',
  wait_for_connect: 'Wait for Connect',
  voice_note: 'Voice Note',
  sift: 'Filter',
};

function getStepStateIcon(state: CadenceStepState) {
  switch (state) {
    case 'finished':
    case 'responded':
      return <General.CheckCircle className="size-3.5 text-green-500" />;
    case 'canceled':
    case 'interrupted':
      return <General.XCircle className="size-3.5 text-red-500" />;
    case 'skipped':
      return <General.Minus className="size-3.5 text-slate-400" />;
    case 'not-started':
      return <Time.Clock className="size-3.5 text-slate-400" />;
    case 'action-required':
      return <Alerts.AlertCircle className="size-3.5 text-yellow-500" />;
    case 'contact_unavailable':
      return <General.XCircle className="size-3.5 text-slate-400" />;
    default:
      return <General.HelpCircle className="size-3.5 text-slate-400" />;
  }
}

function formatDelay(ms: number, alignWeek?: boolean): string {
  if (alignWeek) return weekDelayLabel(Math.max(1, Math.round(ms / WEEK_MS)));
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  const minutes = Math.floor(ms / (1000 * 60));
  return `${minutes}m`;
}

function StepGraphRenderer({ graph }: { graph: CadenceGraph<CampaignDetailStep> }) {
  if (!graph.startId || !graph.nodes[graph.startId]) {
    return <p className="text-sm text-slate-500">No steps configured</p>;
  }

  return (
    <div className="flex flex-col">
      <StepNodeRenderer nodeId={graph.startId} nodes={graph.nodes} depth={0} />
    </div>
  );
}

type StepNodeRendererProps = {
  nodeId: string;
  nodes: Record<string, CadenceGraphNode<CampaignDetailStep>>;
  depth: number;
};

function StepNodeRenderer({ nodeId, nodes, depth }: StepNodeRendererProps) {
  const node = nodes[nodeId];
  if (!node) return null;

  const isWaitForConnect = node.type === 'wait_for_connect';
  const isDelay = node.type === 'delay';

  if (isDelay) {
    return (
      <>
        <StepRow step={node} depth={depth} />
        {node.group.length > 0 && (
          <div className="ml-4 border-l-2 border-slate-200 pl-2 dark:border-slate-700">
            {node.group.map((groupedId) => (
              <StepNodeRenderer key={groupedId} nodeId={groupedId} nodes={nodes} depth={depth + 1} />
            ))}
          </div>
        )}
        {node.next.one && <StepNodeRenderer nodeId={node.next.one} nodes={nodes} depth={depth} />}
      </>
    );
  }

  if (isWaitForConnect) {
    const acceptedPath = node.next.two; // 'two' = accepted
    const rejectedPath = node.next.one; // 'one' = rejected/timeout

    return (
      <>
        <StepRow step={node} depth={depth} />
        <div className="ml-4 flex flex-col gap-1">
          {acceptedPath && (
            <div className="border-l-2 border-green-300 pl-2 dark:border-green-700">
              <div className="mb-1 text-xs font-medium text-green-600 dark:text-green-400">If accepted:</div>
              <StepNodeRenderer nodeId={acceptedPath} nodes={nodes} depth={depth + 1} />
            </div>
          )}
          {rejectedPath && (
            <div className="border-l-2 border-slate-300 pl-2 dark:border-slate-600">
              <div className="mb-1 text-xs font-medium text-slate-500 dark:text-slate-400">If not accepted:</div>
              <StepNodeRenderer nodeId={rejectedPath} nodes={nodes} depth={depth + 1} />
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <StepRow step={node} depth={depth} />
      {node.next.one && <StepNodeRenderer nodeId={node.next.one} nodes={nodes} depth={depth} />}
    </>
  );
}

function StepRow({ step }: { step: CadenceGraphNode<CampaignDetailStep>; depth: number }) {
  const [errorExpanded, setErrorExpanded] = React.useState(false);
  const isCompleted = step.state === 'finished' || step.state === 'responded';
  const isErrored = step.state === 'canceled' || step.state === 'interrupted';
  const isSkipped = step.state === 'skipped';

  const errorOrReason = step.errorMessage || step.skipReason || step.internalErrorMessage;

  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 rounded-sm px-2 py-1.5',
        isErrored && 'bg-red-50/50 dark:bg-red-900/10',
        isSkipped && 'opacity-60',
      )}>
      <div className="flex items-center gap-2">
        {getStepStateIcon(step.state)}
        <span
          className={cn(
            'flex-1 text-sm',
            isCompleted ? 'text-slate-700 dark:text-slate-300' : 'text-slate-500 dark:text-slate-400',
          )}>
          {stepTypeLabels[step.type]}
          {step.type === 'delay' && step.delay > 0 && (
            <span className="ml-1 text-xs text-slate-400">({formatDelay(step.delay, step.delayAlignWeek)})</span>
          )}
        </span>
        {step.completedOn && (
          <span className="text-xs text-slate-500 dark:text-slate-400">{formatDate(step.completedOn)}</span>
        )}
      </div>
      {errorOrReason && (
        <div
          onClick={() => setErrorExpanded(!errorExpanded)}
          className={cn(
            'ml-5 cursor-pointer text-xs',
            isErrored ? 'text-red-500' : 'text-slate-500 dark:text-slate-400',
            !errorExpanded && 'line-clamp-1',
          )}>
          {errorOrReason}
        </div>
      )}
    </div>
  );
}

function StepFlatRow({ step }: { step: CampaignDetailStep }) {
  const [errorExpanded, setErrorExpanded] = React.useState(false);
  const isCompleted = step.state === 'finished' || step.state === 'responded';
  const isErrored = step.state === 'canceled' || step.state === 'interrupted';
  const isSkipped = step.state === 'skipped';

  const errorOrReason = step.errorMessage || step.skipReason || step.internalErrorMessage;

  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 rounded-sm px-2 py-1.5',
        isErrored && 'bg-red-50/50 dark:bg-red-900/10',
        isSkipped && 'opacity-60',
      )}>
      <div className="flex items-center gap-2">
        {getStepStateIcon(step.state)}
        <span
          className={cn(
            'flex-1 text-sm',
            isCompleted ? 'text-slate-700 dark:text-slate-300' : 'text-slate-500 dark:text-slate-400',
          )}>
          {stepTypeLabels[step.type]}
          {step.type === 'delay' && step.delay > 0 && (
            <span className="ml-1 text-xs text-slate-400">({formatDelay(step.delay, step.delayAlignWeek)})</span>
          )}
        </span>
        {step.completedOn && (
          <span className="text-xs text-slate-500 dark:text-slate-400">{formatDate(step.completedOn)}</span>
        )}
      </div>
      {errorOrReason && (
        <div
          onClick={() => setErrorExpanded(!errorExpanded)}
          className={cn(
            'ml-5 cursor-pointer text-xs',
            isErrored ? 'text-red-500' : 'text-slate-500 dark:text-slate-400',
            !errorExpanded && 'line-clamp-1',
          )}>
          {errorOrReason}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main pure component
// ============================================================================

/**
 * Presentational read-only view of a single campaign (cadence) for a contact:
 * header, step graph, and interaction history. No data fetching — the container
 * supplies the campaign header, steps, history and loading flags.
 */
export function CampaignDetailPagePure({
  currentUserId,
  campaignHeader,
  steps,
  interruptMessage,
  interruptReason,
  history,
  isLoading,
  isHistoryLoading,
  onBack,
}: CampaignDetailPagePureProps) {
  const stepsGraph = steps.length > 0 ? buildStepGraphIncludingArchived(steps) : null;
  const reachableIds = stepsGraph ? getReachableNodeIds(stepsGraph) : new Set<string>();
  const orphanedSteps = steps.filter((s) => !reachableIds.has(s.id));

  if (isLoading) {
    return (
      <SmallPaddedScrollable className="flex items-center justify-center">
        <WaveSpinner />
      </SmallPaddedScrollable>
    );
  }

  if (!campaignHeader) {
    return (
      <SmallPaddedScrollable className="flex flex-col items-center justify-center gap-2 text-slate-500 dark:text-slate-400">
        <General.InfoCircle className="size-8 opacity-50" />
        <p className="text-sm">Campaign not found</p>
        <Button size="sm" onClick={onBack} icon={<Arrows.ArrowLeft />}>
          Back to list
        </Button>
      </SmallPaddedScrollable>
    );
  }

  return (
    <SmallPaddedScrollable innerClassName="p-0">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
        <div className="flex items-center gap-2">
          <Button size="xs" color="blue" tone="outline" onClick={onBack} icon={<Arrows.ArrowLeft />} />
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-900 dark:text-slate-100">{campaignHeader.name}</span>
              <Badge size="xs" type={getStateBadgeType(campaignHeader.state)} dot>
                {getStateLabel(campaignHeader.state)}
              </Badge>
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
              <span>Created: {formatDate(campaignHeader.createdOn)}</span>
              <span>
                Steps: {campaignHeader.stepsRun ?? 0} / {campaignHeader.stepsTotalCount}
              </span>
            </div>
            {(interruptMessage || interruptReason) && (
              <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                {interruptReason && <span className="font-medium">{interruptReason}: </span>}
                {interruptMessage}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Steps Section */}
      {steps.length > 0 && (
        <div className="border-b border-slate-200 dark:border-slate-700">
          <div className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Steps
          </div>
          <div className="px-4 pb-3">
            {stepsGraph && <StepGraphRenderer graph={stepsGraph} />}
            {orphanedSteps.length > 0 && (
              <div className="mt-2 rounded-sm border border-yellow-200 bg-yellow-50 p-2 dark:border-yellow-800 dark:bg-yellow-900/20">
                <div className="mb-1 flex items-center gap-1 text-xs font-medium text-yellow-700 dark:text-yellow-400">
                  <Alerts.AlertCircle className="size-3" />
                  <span>Steps with missing parent relationship:</span>
                </div>
                {orphanedSteps.map((step) => (
                  <StepFlatRow key={step.id} step={step} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Section */}
      <div className="px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        History
      </div>

      {isHistoryLoading && (
        <SmallPadded className="flex items-center justify-center py-8">
          <WaveSpinner />
        </SmallPadded>
      )}

      {!isHistoryLoading && history.length === 0 && (
        <SmallPadded className="flex flex-col items-center justify-center gap-2 py-8 text-slate-500 dark:text-slate-400">
          <Time.ClockRewind className="size-6 opacity-50" />
          <p className="text-sm">No history for this campaign</p>
        </SmallPadded>
      )}

      {!isHistoryLoading && history.length > 0 && (
        <SmallPadded className="bg-white dark:bg-slate-900">
          <ContentSection>
            {history.map((interactionObj, i) => {
              const { interaction, interactionUser, documentView, cadence, contact } = interactionObj;
              const prevDate = history[i - 1]?.interaction?.date ?? null;
              return (
                <InteractionStep
                  key={interaction.id}
                  currentUserId={currentUserId}
                  contactFirstName={contact?.first_name}
                  contactLastName={contact?.last_name}
                  interactionId={String(interaction.id)}
                  interactionUserId={interaction.userId}
                  interactionAction={interaction.interactionAction as InteractionAction}
                  interactionDate={interaction.date}
                  interactionUpdatedOn={interaction.updatedOn}
                  interactionSubject={interaction.subject}
                  interactionBody={interaction.body}
                  interactionFromNumber={interaction.from}
                  interactionToNumber={interaction.to}
                  interactionDataUrl={interaction.data_url}
                  interactionDataUrlType={interaction.data_url_type}
                  actorName={interactionUser?.name}
                  actorEmail={interactionUser?.email ?? ''}
                  documentId={documentView?.doc_id}
                  documentTitle={documentView?.title}
                  documentDescription={documentView?.description}
                  documentOgImageUrl={documentView?.og_image_url}
                  documentCity={documentView?.fp_ip_loc_city_name}
                  documentCountry={documentView?.fp_ip_loc_country_name}
                  documentBrowserName={documentView?.fp_browser_name}
                  documentOs={documentView?.fp_os}
                  cadenceName={cadence?.name}
                  prevInteractionDate={prevDate}
                  isLast={i === history.length - 1}
                  onClickToRespond={() => {}}
                  onMarkMeetingBooked={() => {}}
                />
              );
            })}
          </ContentSection>
        </SmallPadded>
      )}
    </SmallPaddedScrollable>
  );
}
